import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Event, EventWithRelations } from "../../../services/EventService";
import { supabase } from '../../../config/supabase';
import { UserService, ConnectionWithProfile } from '../../../services/UserService';
import { RootState } from '../../../config/store';

export interface UserEventWithType extends EventWithRelations {
  user_role: 'host' | 'co-host' | 'attending' | 'user';
  host_status?: 'pending' | 'accepted' | 'declined';
}

interface DashboardState {
  hostedEvents: UserEventWithType[];
  attendingEvents: UserEventWithType[];
  userEvents: UserEventWithType[]; // Nuevo: para event_users
  isLoading: boolean;
  error: string | null;
  lastFetch: number | null;
  connections: {
    following: ConnectionWithProfile[];
    followers: ConnectionWithProfile[];
  };
  connectionsLoading: boolean;
  connectionsError: string | null;
}

const initialState: DashboardState = {
  hostedEvents: [],
  attendingEvents: [],
  userEvents: [], // Nuevo
  isLoading: false,
  error: null,
  lastFetch: null,
  connections: {
    following: [],
    followers: []
  },
  connectionsLoading: false,
  connectionsError: null,
};

// Async Thunks
export const fetchUserEvents = createAsyncThunk(
  'dashboard/fetchUserEvents',
  async (userId: string, { rejectWithValue }) => {
    try {
      // 1. Obtener eventos que el usuario creó
      const { data: createdEvents, error: createdError } = await Event.getByUser(userId, true);
      
      // 2. Obtener eventos donde es participante (event_users)
      const { data: userEvents, error: userError } = await Event.getUserEvents(userId);
      
      // 3. Obtener eventos donde asiste (event_attendees) 
      const { data: attendingEvents, error: attendingError } = await Event.getAttendingEvents(userId);

      // 4. Obtener eventos donde es co-host
      const { data: coHostRelations, error: coHostError } = await supabase
        .from('event_hosts')
        .select('*')
        .eq('user_id', userId)
        .eq('is_main_host', false);

      let coHostEvents: EventWithRelations[] = [];
      if (coHostRelations && coHostRelations.length > 0) {
        const coHostEventIds = coHostRelations.map(rel => rel.event_id);
        
        const { data: coHostEventsData, error: coHostEventsError } = await supabase
          .from('events')
          .select('*')
          .in('id', coHostEventIds);

        if (coHostEventsData) {
          for (const event of coHostEventsData) {
            const { data: eventHosts } = await supabase
              .from('event_hosts')
              .select('*')
              .eq('event_id', event.id);

            coHostEvents.push({
              ...event,
              hosts: eventHosts || [],
              side_events: [],
              hosts_count: eventHosts?.length || 0,
              attendees_count: 0
            });
          }
        }
      }

      // Concatenar todos los eventos
      const allEvents = [
        ...(createdEvents || []),
        ...(userEvents || []),
        ...(attendingEvents || []),
        ...coHostEvents
      ];

      // Remover duplicados por ID
      const uniqueEvents = allEvents.filter((event, index, self) => 
        index === self.findIndex((e) => e.id === event.id)
      );

      // Separar por tipo
      const hostedEvents: UserEventWithType[] = [];
      const attendingEventsFiltered: UserEventWithType[] = [];
      const userEventsFiltered: UserEventWithType[] = []; // Nuevo

      uniqueEvents.forEach(event => {
        // CASO 1: Es creador del evento
        if (event.created_by === userId || event.creator_id === userId) {
          hostedEvents.push({
            ...event,
            user_role: 'host'
          });
          return;
        }

        // CASO 2: Es co-host (verificar en coHostRelations)
        if (coHostRelations?.some(rel => rel.event_id === event.id)) {
          const coHostRel = coHostRelations.find(rel => rel.event_id === event.id);
          hostedEvents.push({
            ...event,
            user_role: 'co-host',
            host_status: coHostRel?.status || 'pending'
          });
          return;
        }

        // CASO 3: Es host principal (verificar en hosts del evento)
        if (event.hosts?.some((h: any) => h.user_id === userId && h.is_main_host)) {
          hostedEvents.push({
            ...event,
            user_role: 'host'
          });
          return;
        }

        // CASO 4: Es co-host (verificar en hosts del evento)
        if (event.hosts?.some((h: any) => h.user_id === userId && !h.is_main_host)) {
          const hostRel = event.hosts.find((h: any) => h.user_id === userId && !h.is_main_host);
          hostedEvents.push({
            ...event,
            user_role: 'co-host',
            host_status: hostRel?.status || 'pending'
          });
          return;
        }

        // CASO 5: Viene de event_users
        if (userEvents?.some(ue => ue.id === event.id)) {
          userEventsFiltered.push({
            ...event,
            user_role: 'user'
          });
          return;
        }

        // CASO 6: Es participante (attendees)
        attendingEventsFiltered.push({
          ...event,
          user_role: 'attending'
        });
      });

      return {
        hostedEvents,
        attendingEvents: attendingEventsFiltered,
        userEvents: userEventsFiltered // Nuevo
      };

    } catch (error: any) {
      console.error('Unexpected error fetching user events:', error);
      return rejectWithValue(error.message || 'Failed to fetch events');
    }
  }
);

// Nuevo thunk para obtener conexiones
export const fetchUserConnections = createAsyncThunk(
  'dashboard/fetchUserConnections',
  async (userId: string, { rejectWithValue }) => {
    try {
      const { data, error } = await UserService.getConnectionsWithProfiles(userId);
      
      if (error) {
        return rejectWithValue(error.message || 'Error fetching connections');
      }
      
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error fetching connections');
    }
  }
);

// Nuevo thunk para seguir usuario
export const followUser = createAsyncThunk(
  'dashboard/followUser',
  async ({ userFromId, userToId }: { userFromId: string; userToId: string }, { rejectWithValue, dispatch }) => {
    try {
      const { data, error } = await UserService.follow(userFromId, userToId);
      
      if (error) {
        return rejectWithValue(error.message || 'Error following user');
      }
      
      // Refrescar conexiones después de seguir
      dispatch(fetchUserConnections(userFromId));
      
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error following user');
    }
  }
);

// Nuevo thunk para dejar de seguir usuario
export const unfollowUser = createAsyncThunk(
  'dashboard/unfollowUser',
  async ({ userFromId, userToId }: { userFromId: string; userToId: string }, { rejectWithValue, dispatch }) => {
    try {
      const { success, error } = await UserService.unfollow(userFromId, userToId);
      
      if (!success) {
        return rejectWithValue(error?.message || 'Error unfollowing user');
      }
      
      // Refrescar conexiones después de dejar de seguir
      dispatch(fetchUserConnections(userFromId));
      
      return { userToId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error unfollowing user');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: (state) => {
      return initialState;
    },
    optimisticUpdateHostStatus: (state, action: PayloadAction<{ hostId: string; status: 'accepted' | 'declined' }>) => {
      const { hostId, status } = action.payload;
      
      state.hostedEvents = state.hostedEvents.map(event => {
        const host = event.hosts?.find(h => h.id === hostId);
        if (host) {
          return {
            ...event,
            host_status: status,
            hosts: event.hosts?.map(h => 
              h.id === hostId ? { ...h, status } : h
            )
          };
        }
        return event;
      });
    },
    clearConnectionsError: (state) => {
      state.connectionsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hostedEvents = action.payload.hostedEvents;
        state.attendingEvents = action.payload.attendingEvents;
        state.userEvents = action.payload.userEvents; // Nuevo
        state.lastFetch = Date.now();
        state.error = null;
      })
      .addCase(fetchUserEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch connections
      .addCase(fetchUserConnections.pending, (state) => {
        state.connectionsLoading = true;
        state.connectionsError = null;
      })
      .addCase(fetchUserConnections.fulfilled, (state, action) => {
        state.connectionsLoading = false;
        if (action.payload) {
          state.connections = action.payload;
        }
      })
      .addCase(fetchUserConnections.rejected, (state, action) => {
        state.connectionsLoading = false;
        state.connectionsError = action.payload as string;
      })
    // Follow user
      .addCase(followUser.pending, (state) => {
        // Opcional: mostrar loading en UI
      })
      .addCase(followUser.fulfilled, (state, action) => {
        // Conexiones se actualizarán automáticamente con fetchUserConnections
      })
      .addCase(followUser.rejected, (state, action) => {
        state.connectionsError = action.payload as string;
      })
    // Unfollow user
      .addCase(unfollowUser.pending, (state) => {
        // Opcional: mostrar loading en UI
      })
      .addCase(unfollowUser.fulfilled, (state, action) => {
        // Conexiones se actualizarán automáticamente con fetchUserConnections
      })
      .addCase(unfollowUser.rejected, (state, action) => {
        state.connectionsError = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearConnectionsError, // Nuevo
  optimisticUpdateHostStatus 
} = dashboardSlice.actions;

// Selectors
export const selectDashboardState = (state: { dashboard: DashboardState }) => state.dashboard;
export const selectHostedEvents = (state: { dashboard: DashboardState }) => state.dashboard.hostedEvents;
export const selectAttendingEvents = (state: { dashboard: DashboardState }) => state.dashboard.attendingEvents;
export const selectUserEvents = (state: { dashboard: DashboardState }) => state.dashboard.userEvents; // Nuevo
export const selectAllUserEvents = (state: { dashboard: DashboardState }) => [
  ...state.dashboard.hostedEvents,
  ...state.dashboard.attendingEvents,
  ...state.dashboard.userEvents // Nuevo
];

// Helper selectors
export const selectPendingCoHostInvitations = (state: { dashboard: DashboardState }) => 
  state.dashboard.hostedEvents.filter(event => 
    event.user_role === 'co-host' && event.host_status === 'pending'
  );

export const selectEventsNeedingAttention = (state: { dashboard: DashboardState }) => {
  const pendingInvitations = selectPendingCoHostInvitations(state);
  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = [
    ...state.dashboard.hostedEvents,
    ...state.dashboard.attendingEvents,
    ...state.dashboard.userEvents // Nuevo
  ].filter(event => event.start_date >= today);
  
  return {
    pendingInvitations: pendingInvitations.length,
    upcomingEvents: upcomingEvents.length
  };
};

// Nuevos selectores para conexiones
export const selectConnections = (state: RootState) => state.dashboard.connections;
export const selectConnectionsLoading = (state: RootState) => state.dashboard.connectionsLoading;
export const selectConnectionsError = (state: RootState) => state.dashboard.connectionsError;
export const selectFollowingConnections = (state: RootState) => state.dashboard.connections.following;
export const selectFollowersConnections = (state: RootState) => state.dashboard.connections.followers;

export default dashboardSlice.reducer;